import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, Lock } from "lucide-react";
import { Seo } from "../components/layout/Seo";
import { PageHero } from "../components/layout/PageHero";
import { api, apiReady, formatApiError } from "../lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-KE", { dateStyle: "long" }) : "");

const Stepper = ({ steps, step }) => {
  const reduce = useReducedMotion();
  return (
    <ol className="relative mt-10 grid gap-6 sm:grid-cols-5 sm:gap-2" data-testid="track-stepper">
      <span aria-hidden="true" className="absolute left-[15px] top-0 h-full w-px bg-white/10 sm:left-0 sm:top-[15px] sm:h-px sm:w-full" />
      <motion.span aria-hidden="true" initial={reduce ? false : { scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-[15px] top-0 w-px origin-top bg-gradient-to-b from-[#C2410C] to-[#FB923C] sm:hidden" style={{ height: `${(step / (steps.length - 1 || 1)) * 100}%` }} />
      <motion.span aria-hidden="true" initial={reduce ? false : { scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-0 top-[15px] hidden h-px origin-left bg-gradient-to-r from-[#C2410C] to-[#FB923C] sm:block" style={{ width: `${(step / (steps.length - 1 || 1)) * 80}%` }} />
      {steps.map((label, i) => {
        const done = i < step || (i === step && step === steps.length - 1);
        const current = i === step && !done;
        return (
          <li key={label} className="relative flex items-center gap-4 sm:flex-col sm:items-start sm:gap-3" aria-current={current ? "step" : undefined}
            data-testid={`track-step-${i}`}>
            <span className={`relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full border text-xs transition-colors ${done ? "border-orange-500 bg-orange-500 text-black" : current ? "border-orange-400 bg-[#1A1411] text-orange-300 shadow-[0_0_0_6px_rgba(249,115,22,0.12)]" : "border-white/15 bg-[#0C0A09] text-stone-500"}`}>
              {done ? <Check className="h-4 w-4" /> : i + 1}
            </span>
            <span className={`text-sm ${current ? "font-semibold text-white" : done ? "text-stone-200" : "text-stone-400"}`}>{label}</span>
          </li>
        );
      })}
    </ol>
  );
};

const Result = ({ r }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-[26px] border border-white/10 bg-[#120F0D] p-6 sm:p-10" data-testid="track-result">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone-400">Reference</p>
        <p className="mt-2 font-mono text-2xl tracking-wider text-orange-400" data-testid="track-reference">{r.reference}</p>
      </div>
      <span className="rounded-full border border-orange-500/40 bg-orange-500/10 px-4 py-1.5 text-sm font-medium text-orange-200" data-testid="track-status">{r.status_label}</span>
    </div>
    <dl className="mt-8 grid gap-4 border-y border-white/10 py-6 sm:grid-cols-3">
      {[["Category", r.category], ["Submitted", fmtDate(r.submitted_at)], ["Status updated", fmtDate(r.status_updated_at)]].map(([k, v]) => (
        <div key={k}><dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-400">{k}</dt><dd className="mt-1.5 text-stone-100">{v}</dd></div>
      ))}
    </dl>
    <Stepper steps={Array.isArray(r.steps) ? r.steps : []} step={r.step ?? 0} />
    <p className="mt-10 rounded-2xl bg-white/[0.03] p-5 text-[15px] leading-relaxed text-stone-200" data-testid="track-guidance">{r.guidance}</p>
    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
      <Link to={`/contact?type=contact&ref=${encodeURIComponent(r.reference)}`} data-testid="track-assist-btn"
        className="tc-btn-primary inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold">Ask for further assistance <ArrowUpRight className="h-4 w-4" /></Link>
    </div>
  </motion.div>
);

export default function Track() {
  const { search, hash } = useLocation();
  const [reference, setReference] = useState(() => new URLSearchParams(search).get("ref") ?? "");
  const [token, setToken] = useState(() => new URLSearchParams(hash.replace(/^#/, "")).get("t") ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const lookup = useCallback(async (ref, tok) => {
    if (!ref.trim() || tok.trim().length < 16) { setError("Enter your reference number and the private tracking code from your confirmation."); return; }
    if (!apiReady) { setError("Tracking is not connected on this deployment yet."); return; }
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/track", { reference: ref.trim(), token: tok.trim() });
      setResult(data ?? null);
    } catch (err) {
      setResult(null);
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    // Re-run whenever a new tracking link is opened (SPA navigation), always clearing any previous result first.
    const ref = new URLSearchParams(search).get("ref") ?? "";
    const tok = new URLSearchParams(hash.replace(/^#/, "")).get("t") ?? "";
    if (!hash && !search) return;
    setResult(null);
    setError("");
    if (ref) setReference(ref);
    if (tok) setToken(tok);
    if (ref && tok) lookup(ref, tok);
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, [search, hash, lookup]);

  const inputCls = "mt-2 h-12 w-full rounded-xl border border-white/15 bg-black/40 px-4 text-stone-100 outline-none transition-[border-color] placeholder:text-stone-500 focus:border-orange-500";

  return (
    <>
      <Seo title="Track My Request | TechCare Technologies" path="/track" description="Securely check the progress of your TechCare Technologies enquiry using your reference and private tracking code." />
      <PageHero eyebrow="Track my request" lines={["Your request,", <span key="c" className="tc-copper-text">in clear view.</span>]} testid="track-hero"
        lead="Check progress using your reference number and private tracking code. We only show the category, dates and general status — never your personal details." />
      <section className="bg-[#0C0A09] pb-24 text-stone-100 sm:pb-32">
        <div className="mx-auto grid max-w-[1440px] gap-8 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
          <form onSubmit={(e) => { e.preventDefault(); lookup(reference, token); }} noValidate data-testid="track-form"
            className="h-fit rounded-[26px] border border-white/10 bg-[#120F0D] p-6 sm:p-8 lg:col-span-4">
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-orange-400"><Lock className="h-3.5 w-3.5" /> Secure lookup</p>
            <label htmlFor="t-ref" className="mt-6 block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Reference number</label>
            <input id="t-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="TC-2610-XXXXXX" autoCapitalize="characters" maxLength={40}
              className={`${inputCls} font-mono uppercase`} data-testid="track-ref-input" />
            <label htmlFor="t-tok" className="mt-5 block font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Private tracking code</label>
            <input id="t-tok" type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" maxLength={80}
              className={`${inputCls} font-mono`} data-testid="track-token-input" />
            {error && <p className="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert" data-testid="track-error">{error}</p>}
            <button disabled={busy} className="tc-btn-primary mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-semibold" data-testid="track-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Check status
            </button>
            <p className="mt-5 text-xs leading-relaxed text-stone-400">Your tracking code was shown after you submitted your request. Lost it? <Link to="/contact?type=contact" className="text-orange-300 underline">Contact us</Link> and quote your reference.</p>
          </form>
          <div className="lg:col-span-8">
            {result ? <Result r={result} /> : (
              <div className="grid h-full min-h-[280px] place-items-center rounded-[26px] border border-dashed border-white/10 p-8 text-center text-sm text-stone-400" data-testid="track-empty">
                Enter your details to see the progress of your request.
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
